import ClickAwayListener from '@mui/material/ClickAwayListener';
import clsx from 'clsx';
import GoogleMapReact from 'google-map-react';
import React, { RefObject, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSupercluster from 'use-supercluster';
import config from '../config';
import { getPropertyThumbnail } from '../helpers/images';
import { getFormattedPrice } from '../helpers/price';
import { getSingularPluralWord } from '../i18n/translations';
import styles from './PropertiesGoogleMap.module.scss';
import { Property } from './Property/Property';
import { PropertyNoBimmo } from './PropertyNoBimmo/PropertyNoBimmo';

const DEFAULT_ZOOM = 13;
const MAX_ZOOM = 16;

const marginOffsetsForClusterItems = [
  '',
  '-30px 0 0 -30px',
  '-54px 0 0 -50px',
  '-30px 0 0 80px',
  '20px 0 0 80px',
  '20px 0 0 -30px',
  '40px 0 0 -70px',
  '70px 0 0 -70px',
  '50px 0 0 30px',
  '70px 0 0 30px',
  '0px 0 0 -110px',
  '-50px 0 0 60px',
  '-3px 0 0 100px',
  '30px 0 0 120px',
  '46px 0 0 120px',
  '61px 0 0 120px',
  '-50px 0 0 -140px',
  '-36px 0 0 -140px',
  '-26px 0 0 -130px',
  '-14px 0 0 -130px',
];

export interface Props {
  onClick?: () => void;
  onDrag?: () => void;
  mapDivRef: RefObject<HTMLDivElement>;
  properties: any[];
  avgPrices?: any[];
}

function getTooltipPosition({
  mapRect,
  markerRect,
  tooltipWidth,
  tooltipHeight,
}: {
  mapRect?: DOMRect;
  markerRect?: DOMRect;
  tooltipWidth: number;
  tooltipHeight: number;
}) {
  const defaultStyles = {
    marginLeft: -tooltipWidth / 2,
    left: '50%',
    top: '100%',
  };

  if (!mapRect || !markerRect)
    return { pos: 'bottom center', styles: defaultStyles };

  const leftSpace = markerRect.x - mapRect.x;
  const rightSpace =
    mapRect.x + mapRect.width - markerRect.x - markerRect.width;
  const hasLeftSpace = leftSpace > tooltipWidth / 2;
  const hasRightSpace = rightSpace > tooltipWidth / 2;

  const topSpace = markerRect.y - mapRect.y;
  const bottomSpace =
    mapRect.y + mapRect.height - markerRect.y - markerRect.height;
  const hasTopSpace = topSpace > tooltipHeight;
  const hasBottomSpace = bottomSpace > tooltipHeight;

  if (
    !mapRect ||
    !markerRect ||
    (hasTopSpace && hasBottomSpace && hasLeftSpace && hasRightSpace) ||
    (hasBottomSpace && hasRightSpace && hasLeftSpace)
  ) {
    return { pos: 'bottom center', styles: defaultStyles };
  } else if (
    (hasTopSpace && hasBottomSpace && hasLeftSpace) ||
    (hasBottomSpace && hasLeftSpace)
  ) {
    return {
      pos: 'bottom center left',
      styles: { top: '100%', right: 0, marginRight: -rightSpace },
    };
  } else if (
    (hasTopSpace && hasBottomSpace && hasRightSpace) ||
    (hasBottomSpace && hasRightSpace)
  ) {
    return {
      pos: 'bottom center right',
      styles: { top: '100%', left: 0, marginLeft: -leftSpace },
    };
  } else if (hasTopSpace && hasLeftSpace && hasRightSpace) {
    return {
      pos: 'top center',
      styles: { bottom: '100%', left: '50%', marginLeft: -tooltipWidth / 2 },
    };
  } else if (hasTopSpace && hasLeftSpace) {
    return {
      pos: 'top center left',
      styles: { bottom: '100%', right: 0, marginRight: -rightSpace },
    };
  } else if (hasTopSpace && hasRightSpace) {
    return {
      pos: 'top center right',
      styles: { bottom: '100%', left: 0, marginLeft: -leftSpace },
    };
  } else if (hasTopSpace) {
    return {
      pos: 'top center',
      styles: { bottom: '100%', left: '50%', marginLeft: -tooltipWidth / 2 },
    };
  }

  return { pos: 'bottom center', styles: defaultStyles };
}

const TOOLTIP_WIDTH = 270;
const TOOLTIP_HEIGHT = 270;

function MapMarker(props: {
  property?: any;
  marginOffset?: string;
  mapDivRef: RefObject<HTMLDivElement>;
  onPropertiesSliderShow: (properties: any[] | undefined) => void;
  lat: number;
  lng: number;
  $markerHolderClassName?: string;
  onClick: () => void;
  onClickAway: () => void;
  avgPrices: any;
}) {
  const [tooltipInfo, setTooltipInfo] = useState<any>();
  const markerContentRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    setTooltipInfo(() => {
      let styles: any = {};

      const tooltipPos = getTooltipPosition({
        mapRect: props.mapDivRef?.current?.getBoundingClientRect(),
        markerRect: markerContentRef.current?.getBoundingClientRect(),
        tooltipWidth: TOOLTIP_WIDTH,
        tooltipHeight: TOOLTIP_HEIGHT,
      });

      styles = tooltipPos.styles;

      // because clickAway of other markers were taking precedence
      // update properties on next tick
      window.setTimeout(() => {
        props.onClick();
        props.onPropertiesSliderShow([props.property]);
      }, 0);

      return { property: props.property, styles };
    });
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        props.onClickAway();
        setTooltipInfo(undefined);
        props.onPropertiesSliderShow(undefined);
      }}
    >
      <div className={styles.marker} style={{ margin: props.marginOffset }}>
        <div
          className={styles.markerContent}
          onClick={handleClick}
          ref={markerContentRef}
        >
          <span>
            {getFormattedPrice(
              props.property?.prices?.property
                ? props.property?.prices.property
                : props.property?.price
            )}
          </span>
        </div>

        <div
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {tooltipInfo?.property && (
            <div
              className={styles.markerTooltip}
              style={{
                width: TOOLTIP_WIDTH,
                height: TOOLTIP_HEIGHT,
                ...tooltipInfo.styles,
              }}
            >
              {tooltipInfo.property.idWebSite ? (
                <PropertyNoBimmo
                  type="mapinfo"
                  property={tooltipInfo.property}
                  avgPrices={props.avgPrices}
                />
              ) : (
                <Property
                  type="mapinfo"
                  property={tooltipInfo.property}
                  onTouchStart={(_, e) => e.stopPropagation()}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
}

export default function PropertiesGoogleMap(props: Props) {
  const mapRef = useRef();
  const [bounds, setBounds] = useState<[number, number, number, number]>();
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [activeMarker, setActiveMarker] = useState<{ id: string }>();
  const [sliderInfo, setSliderInfo] = useState<{
    properties: any[] | undefined;
  }>();

  const points = props.properties.reduce((prev, property) => {
    let [lat, lng] = [0, 0];
    if (property.location) {
      [lat, lng] = property.location.coordinates;
    } else {
      if (property.coordinates) {
        if (
          property.coordinates.lat < -4.227109899999999 ||
          property.coordinates.lat > 13.5177999 ||
          property.coordinates.lon < -81.8317 ||
          property.coordinates.lon > -66.8463122
        ) {
          return prev;
        } else {
          lat = property.coordinates.lat;
          lng = property.coordinates.lon;
        }
      } else {
        return prev;
      }
    }

    if (lat === 0 || lng === 0) return prev;

    return [
      ...prev,
      {
        type: 'Feature',
        properties: {
          cluster: false,
          propertyId: property._id,
          property,
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
      },
    ];
  }, []);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: {
      maxZoom: MAX_ZOOM,
      radius: 75,
    },
  });

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: config.maps.apiKey }}
        yesIWantToUseGoogleMapApiInternals
        defaultZoom={DEFAULT_ZOOM}
        onClick={props.onClick}
        onDrag={props.onDrag}
        onGoogleApiLoaded={({ map }) => {
          const bounds = new google.maps.LatLngBounds();
          mapRef.current = map;

          points.forEach((point: any) => {
            const [lat, lng] = point.geometry.coordinates;
            return bounds.extend(new google.maps.LatLng(lng, lat));
          });

          map.fitBounds(bounds);

          map.setZoom(Math.min(map.getZoom(), 15));
        }}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom);
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat,
          ]);
        }}
        options={(maps) => ({
          gestureHandling: 'greedy',
          clickableIcons: false,
          zoomControl: true,
          maxZoom: MAX_ZOOM,
          fullscreenControl: false,
          zoomControlOptions: {
            position: maps.ControlPosition.TOP_RIGHT,
          },
        })}
        defaultCenter={{ lat: 0, lng: 0 }}
      >
        {clusters.map((cluster) => {
          const [lng, lat] = cluster.geometry.coordinates;
          const { id: clusterId, properties: clusterProperties } = cluster;
          const {
            propertyId,
            cluster: isCluster,
            property,
          } = clusterProperties;

          if (isCluster) {
            return supercluster
              .getLeaves(clusterId, 7)
              .map(
                (
                  { properties: { propertyId, property } }: any,
                  idx: number
                ) => (
                  <MapMarker
                    onClick={() => setActiveMarker({ id: propertyId })}
                    onClickAway={() => setActiveMarker(undefined)}
                    onPropertiesSliderShow={() => setSliderInfo(undefined)}
                    mapDivRef={props.mapDivRef}
                    property={property}
                    key={propertyId}
                    marginOffset={
                      idx < 20 ? marginOffsetsForClusterItems[idx] : ''
                    }
                    lat={lat}
                    lng={lng}
                    $markerHolderClassName={clsx(
                      styles.markerContainer,
                      activeMarker?.id === propertyId &&
                        styles.markerContentSelected
                    )}
                    avgPrices={props.avgPrices}
                  />
                )
              );
          }

          return (
            <MapMarker
              onClick={() => setActiveMarker({ id: propertyId })}
              onClickAway={() => setActiveMarker(undefined)}
              onPropertiesSliderShow={() => setSliderInfo(undefined)}
              mapDivRef={props.mapDivRef}
              property={property}
              key={propertyId}
              lat={lat}
              lng={lng}
              $markerHolderClassName={clsx(
                styles.markerContainer,
                activeMarker?.id === propertyId && styles.markerContentSelected
              )}
              avgPrices={props.avgPrices}
            />
          );
        })}
      </GoogleMapReact>

      {sliderInfo?.properties && (
        <div
          onTouchEnd={(e) => e.stopPropagation()}
          className={clsx(styles.propertySlider)}
        >
          <div aria-hidden="true" style={{ flex: '0 0 5%' }} />
          {sliderInfo.properties.map((property) => (
            <div key={property.id} className={styles.propertySlide}>
              <Link
                className={styles.propertyContent}
                to={`/properties/${property.id}`}
              >
                <div className={styles.propertyContentImg}>
                  <img src={getPropertyThumbnail(property.attaches)} />
                </div>
                <div className={styles.propertyContentInfo}>
                  <p style={{ fontWeight: 'bold' }}>
                    {getFormattedPrice(property.prices.property)}
                  </p>
                  <p style={{ fontSize: 13, textTransform: 'capitalize' }}>
                    {property.info?.type}
                  </p>
                  <p style={{ fontSize: 13 }}>
                    <span>
                      {property.info.rooms}{' '}
                      <span style={{ textTransform: 'capitalize' }}>
                        {getSingularPluralWord('room', property.info.rooms)}
                      </span>
                    </span>
                    {' | '}
                    {property.info.toilets}{' '}
                    <span style={{ textTransform: 'capitalize' }}>
                      {getSingularPluralWord('bathroom', property.info.toilets)}
                    </span>
                    {' | '}
                    {property.info.squareMeters} m<small>2</small>
                  </p>
                  <p style={{ color: '#707070', fontSize: 14 }}>
                    {property.address?.name}
                  </p>
                </div>
              </Link>
            </div>
          ))}
          <div aria-hidden="true" style={{ flex: '0 0 5%' }} />
        </div>
      )}
    </div>
  );
}
